import { useMemo } from "react";
import { Box, Flex, Heading, Text } from "@chakra-ui/react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import KpiCard from "./KpiCard";
import { CATEGORICAL, COLORS } from "../../theme/dashboardTokens";
import { ordenarSeries, rotuloDaSerie } from "../../pages/gri-indicators/series";

// GRI-05 (CA03) — visão agregada de todas as empresas. Mesma regra de
// cobertura do GRI-04/GriCompanyDashboard (indicadores GRI têm unidade
// incompatível entre si — nada soma valor bruto entre empresas).
//
// `catalogTotals` vem do backend com as 4 séries conhecidas hoje
// (GriSeriesTotals, metrics.interface.ts) — uma série nova aparece na aba
// (não fica mais escondida), mas sem o total do catálogo o denominador é 0 e
// a cobertura mostra 0% até o backend aprender a série nova também.

function yearColor(index) {
  return CATEGORICAL[index % CATEGORICAL.length];
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Box bg={COLORS.surface} border="1px solid #E2E8F0" borderRadius="8px" p="10px" boxShadow="sm">
      <Text fontSize="13px" fontWeight={600} color={COLORS.ink} mb="4px">
        {label}
      </Text>
      {payload.map((p) => (
        <Text key={p.name} fontSize="12px" color={COLORS.inkMuted}>
          {p.name}: <b>{p.value}%</b>
        </Text>
      ))}
    </Box>
  );
}

export default function GriPortfolioDashboard({ entries, catalogTotals, selectedYears }) {
  const scoped = useMemo(
    () => (entries || []).filter((entry) => selectedYears.includes(entry.year)),
    [entries, selectedYears]
  );

  const companyIds = useMemo(() => [...new Set(scoped.map((e) => e.id_company))], [scoped]);

  // Séries presentes no portfólio, não uma lista fixa de 4 — uma série nova
  // ganha barra sozinha assim que alguma empresa tiver dado dela.
  const seriesDisponiveis = useMemo(
    () => ordenarSeries([...new Set(scoped.map((e) => e.series))]),
    [scoped]
  );

  // Cobertura por empresa: indicadores com dado (nos anos selecionados) / catálogo total.

  // Gráfico: cobertura média (entre empresas) por série, comparando anos.
  const chartRows = useMemo(() => {
    return seriesDisponiveis.map((s) => {
      const row = { series: rotuloDaSerie(s) };
      const total = catalogTotals?.[s] ?? 0;
      selectedYears.forEach((year) => {
        if (companyIds.length === 0 || total === 0) {
          row[`ano_${year}`] = 0;
          return;
        }
        const perCompany = companyIds.map((id) => {
          const withData = new Set(
            scoped
              .filter((e) => e.id_company === id && e.series === s && e.year === year)
              .map((e) => e.id_gri_indicator)
          ).size;
          return withData / total;
        });
        const avg = perCompany.reduce((a, b) => a + b, 0) / perCompany.length;
        row[`ano_${year}`] = Math.round(avg * 100);
      });
      return row;
    });
  }, [scoped, companyIds, catalogTotals, selectedYears, seriesDisponiveis]);

  if (!entries || entries.length === 0) {
    return (
      <Flex bg={COLORS.surface} borderRadius="12px" p={["32px", "48px"]} direction="column" align="center" textAlign="center" gap="8px">
        <Heading size="sm" color={COLORS.ink}>
          Sem dados GRI ainda
        </Heading>
        <Text color={COLORS.inkMuted} fontSize="14px" maxW="420px">
          Quando alguma empresa responder questões vinculadas a indicadores GRI, o portfólio aparece aqui.
        </Text>
      </Flex>
    );
  }

  return (
    <Box>

      <Box bg={COLORS.surface} borderRadius="12px" p={["20px", "28px"]}>
        <Heading size="sm" color={COLORS.ink} mb="4px">
          Cobertura média por série
        </Heading>
        <Text fontSize="12px" color={COLORS.inkMuted} mb="4px">
          Para cada empresa, quantos indicadores daquela série ela reportou no
          ano, dividido pelo total de indicadores da série no catálogo. A barra
          é a média desses percentuais entre as empresas — não a soma dos
          valores, que teriam unidades incompatíveis.
        </Text>
        <Text fontSize="11px" color={COLORS.inkMuted} mb="16px">
          Duas leituras que costumam enganar: o denominador é o catálogo
          INTEIRO da série, então 100% exige reportar todos os indicadores
          dela; e a média considera só as empresas com algum dado nos anos
          selecionados — uma empresa que não reportou nada não puxa a média
          para baixo, mas quem reportou em um ano e não no outro conta como 0%
          no ano vazio.
        </Text>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={chartRows} margin={{ top: 16, right: 8, bottom: 8, left: -12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EDF2F7" vertical={false} />
            <XAxis dataKey="series" tick={{ fontSize: 12, fill: COLORS.inkMuted }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: COLORS.inkMuted }} tickLine={false} axisLine={false} />
            <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(0,115,187,0.06)" }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {selectedYears.map((year, index) => (
              <Bar key={year} dataKey={`ano_${year}`} name={String(year)} fill={yearColor(index)} radius={[4, 4, 0, 0]} maxBarSize={48} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
